import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import { Button, Col, Divider, Row, Select, Tag } from "antd";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { getTranslations } from "../../../utils/helpers/translations";
import { IUpdateDonationService } from "../../../models/donation-service/request";
import { execute } from "../../../utils/helpers/execute";
import styles from "../../trademark/trademark-form/style.module.scss";
import Spin from "../../general/antd/spin";
import { languages } from "../../../utils/helpers/constants";
import FieldBuilder from "../../form-components/field-builder";
import FormItem from "../../general/form-item";
import Controller from "../../form-components/controller";
import ImageUploader from "../../form-components/image-uploader";
import { MediumFor } from "../../../models/medium/enum";
import OldImage from "../../form-components/old-image";
import DonationServiceContextProvider from "../../../context/donation-service/provider";
import DonationServiceContext from "../../../context/donation-service/context";
import { getDonationServiceType, DonationServiceType } from "../../../models/donation-service/enum";
import EndPoints from "../../../services/end-points";
import { includes } from "lodash";
const Option = Select.Option;

const DonationServiceForm = () => {
    const { t } = useTranslation();
    const { details, actions, loading } = useContext(DonationServiceContext);
    const { id } = useParams();
    const navigate = useNavigate();
    const donationServiceType = [1, 2];

    const methods = useForm<any>({
        defaultValues: {
            iconId: details?.icon?.id || undefined,
            type: details?.type || "",
            name: getTranslations(details?.translations?.name) || {},
            description: getTranslations(details?.translations?.description) || {},
            relatedIds: details?.relatedServices?.map(service => service.id) || []
        } as IUpdateDonationService,
    });

    const {
        handleSubmit,
        reset,
        control,
        setValue,
        watch,
        formState: { errors },
    } = methods;

    const selectedType = watch("type");
    const [relatedItems, setRelatedItems] = useState<any[]>([]);
    const [relatedLoading, setRelatedLoading] = useState(false);
    const [keyword, setKeyword] = useState<string>();

    useEffect(() => {
        if (!selectedType) {
            setRelatedItems([]);
            return;
        }

        const fetchRelatedItems = async () => {
            setRelatedLoading(true);
            try {
                let endpoint;
                switch (selectedType) {
                    case DonationServiceType.CHARITY:
                        endpoint = EndPoints.charity.getAllCharities;
                        break;
                    case DonationServiceType.CAMPAIGN:
                        endpoint = EndPoints.campaign.getAllCampaigns;
                        break;
                    // case DonationServiceType.ORPHAN:
                    //     endpoint = EndPoints.orphan.getAllOrphans;
                    //     break;
                    default:
                        return;
                }

                const { data } = await endpoint({
                    keyword: keyword ? `${keyword}` : undefined,
                    light: true,
                    perPage: -1,
                });
                setRelatedItems(data.data);
            } catch (error) {
                console.error("Error fetching related items:", error);
            } finally {
                setRelatedLoading(false);
            }
        };

        fetchRelatedItems();
    }, [selectedType, keyword]);

    useEffect(() => {
        reset({
            iconId: details?.icon?.id || undefined,
            type: details?.type || "",
            name: getTranslations(details?.translations?.name) || {},
            description: getTranslations(details?.translations?.description) || {},
            relatedIds: details?.relatedServices?.map(service => service.id) || []
        } as IUpdateDonationService);
    }, [details]);

    useEffect(() => {
        id && actions.getDetails(Number(id));
    }, [id]);

    const [loadingSubmit, setLoadingSubmit] = useState(false);
    const onSubmit = async (data: any) => {
        await execute({
            callback: async () => {
                setLoadingSubmit(true);
                details
                    ? await actions.updateDonationService(details?.id, data)
                    : await actions.createDonationService(data);
                navigate(-1);
            },
            fallback: (error) => {
            },
            finallyCallback: () => {
                setLoadingSubmit(false);
            },
            throwException: false,
        });
    };

    return (
        <FormProvider {...methods}>
            <form className={styles.container} onSubmit={handleSubmit(onSubmit)}>
                <div>
                    <span className={styles.title}>
                        {details ? t("update_donation_service") : t("add_donation_service")}
                    </span>
                    <Divider className={styles.divider} />
                    {loading.includes("details") ? (
                        <div style={{ height: "60vh" }}>
                            <Spin />
                        </div>
                    ) : (
                        <div className={styles.children}>
                            <Row gutter={[16, 8]}>
                                {/* Icon Upload */}
                                <Col span={24}>
                                    <FormItem label={t("icon")} required>
                                        <Controller
                                            name="iconId"
                                            rules={{ required: true }}
                                            render={({ field: { ref, ...field } }) => (
                                                <ImageUploader
                                                    {...field}
                                                    type="DRAGGER"
                                                    onChange={(response) => field.onChange(response?.id)}
                                                    imageFor={MediumFor.UserProfile}
                                                />
                                            )}
                                        />
                                    </FormItem>
                                </Col>

                                <Col span={12}>
                                    <FieldBuilder
                                        name="type"
                                        label={`${t("type")}`}
                                        width="large"
                                        rules={{ required: true }}
                                        input={{
                                            type: "select",
                                            loading: false,
                                            options: donationServiceType?.map((state, index) => {
                                                return {
                                                    label: t(getDonationServiceType(state)),
                                                    value: state,
                                                };
                                            }),
                                        }}
                                    />
                                </Col>

                                {/* Related items selector - only shown when type is selected */}
                                    {/* Related items selector - only shown when type is selected */}
                                    {selectedType && (
                                        <Col span={24}>
                                            <FormItem label={t(getDonationServiceType(selectedType))}>
                                                <Controller
                                                    name="relatedIds"
                                                    control={control}
                                                    rules={{ required: false }}
                                                    render={({ field: { ...field } }) => {
                                                        return (
                                                            <Select
                                                                loading={relatedLoading}
                                                                {...field}
                                                                onSearch={(val) => {
                                                                    setKeyword(val);
                                                                }}
                                                                style={{ width: "100%" }}
                                                                allowClear
                                                                mode="multiple"
                                                                placeholder={t(getDonationServiceType(selectedType))}
                                                                filterOption={false}
                                                                showSearch
                                                                optionLabelProp="label"
                                                            >
                                                                {relatedItems
                                                                    .filter((item) =>
                                                                        keyword ? item?.name?.includes(keyword) ||
                                                                            `${item.firstName} ${item.lastName}`.includes(keyword)
                                                                            : true
                                                                    )
                                                                    .map((item, index) => {
                                                                        const itemName = item.firstName && item.lastName
                                                                            ? `${item.firstName} ${item.lastName}`
                                                                            : item.name;

                                                                        return (
                                                                            <Option
                                                                                key={`${index}`}
                                                                                value={item?.id}
                                                                                label={<Tag>{itemName}</Tag>}
                                                                            >
                                                                                <Tag>{itemName}</Tag>
                                                                            </Option>
                                                                        );
                                                                    })}
                                                            </Select>
                                                        );
                                                    }}
                                                />
                                            </FormItem>
                                        </Col>
                                    )}

                                {/* Name Fields */}
                                {languages?.map((lang, index) => (
                                    <Col span={12} key={index}>
                                        <FieldBuilder
                                            name={`name[${lang?.code}]`}
                                            label={`${t("name")} (${lang?.name})`}
                                            input={{ type: "text" }}
                                            rules={{ required: true }}
                                        />
                                    </Col>
                                ))}

                                {/* Description Fields */}
                                {languages?.map((lang, index) => (
                                    <Col span={12} key={index}>
                                        <FieldBuilder
                                            name={`description[${lang?.code}]`}
                                            label={`${t("description")} (${lang?.name})`}
                                            input={{ type: "text-area" }}
                                        />
                                    </Col>
                                ))}

                                {/* Preview existing icon */}
                                {details?.icon && (
                                    <Col span={24}>
                                        <OldImage src={details?.icon?.url} />
                                    </Col>
                                )}
                            </Row>
                        </div>
                    )}
                </div>
                <div>
                    <div className={styles.formFooter}>
                        <Button onClick={() => navigate(-1)}>
                            {t("cancel")}
                        </Button>
                        <Button loading={loadingSubmit} htmlType="submit" type="primary">
                            {t("save")}
                        </Button>
                    </div>
                </div>
            </form>
        </FormProvider>
    );
};

const DonationServiceFormWrapper = () => {
    return (
        <DonationServiceContextProvider>
            <DonationServiceForm />
        </DonationServiceContextProvider>
    );
};

export default DonationServiceFormWrapper;